import { useEffect, useMemo, useState } from 'react';
import { getSupabaseClient } from '../../data/supabase-client';
import { createSupabasePhotoImageUrls } from '../../data/photos/photo-images';
import type { SolPhotoSourceCard } from '../../data/sol/sol-contract';
import { sessionOwnerUserId, useOptionalSession } from '../auth/session';
import { solPhotoThumbPath } from './sol-photo-display';

/**
 * The short-lived read URL for one cited photo's THUMBNAIL, or null. M11 / I-071.
 *
 * This is the Capture screen's own read path, reused as it stands: the PRIVATE
 * bucket, a five-minute signed URL from `photo-images.ts` — the one module
 * allowed to read a photo from Storage — issued to the owner and to nobody
 * else. Nothing here makes a photo public, lengthens a link, or embeds one.
 *
 * **THE SERVER DECIDED WHICH PHOTOS; THE CLIENT ONLY SHOWS THEM.** A card
 * exists because the database returned a LIVE, DELIVERED photo the caller
 * owns; this hook turns that card into pixels and decides nothing else. A
 * removed photo never reaches here, because it never became a card.
 *
 * A failed signing is an absent picture and a card that still reads correctly
 * — the label, the Capture and the link carry the meaning; the thumbnail is the
 * part Brandon asked to see.
 */
export function useSolPhotoThumbUrl(card: SolPhotoSourceCard): string | null {
  const client = getSupabaseClient();
  const session = useOptionalSession();
  const ownerUserId = sessionOwnerUserId(session);
  const urls = useMemo(() => (client ? createSupabasePhotoImageUrls(client) : null), [client]);
  const path = solPhotoThumbPath(card, ownerUserId);
  // The URL is remembered WITH the path it was issued for, so a card whose
  // path changes never shows the previous photo's picture for a frame.
  const [issued, setIssued] = useState<{ path: string; url: string } | null>(null);

  useEffect(() => {
    if (!urls || path === null) return;
    let cancelled = false;
    void urls.signedUrl(path).then((next) => {
      if (!cancelled && next !== null) setIssued({ path, url: next });
    });
    return () => {
      cancelled = true;
    };
  }, [urls, path]);

  return path !== null && issued?.path === path ? issued.url : null;
}
